import type { FC } from "react";
import { cn } from "@/lib/cn";

type StressPatternProps = {
  syllableCount: number;
  stressIndex: number;
  scale?: "default" | "large";
  active?: number | null;
  syllableLabels?: readonly string[];
  className?: string;
};

export const StressPattern: FC<StressPatternProps> = ({
  syllableCount,
  stressIndex,
  scale = "default",
  active,
  syllableLabels,
  className,
}) => {
  const bars = Array.from({ length: syllableCount });
  const renderBar = (index: number) => (
    <div
      className={cn(
        "rounded-full transition-[background-color,box-shadow] duration-100 motion-reduce:transition-none",
        scale === "large" ? "w-7" : "w-3",
        index === stressIndex ? "bg-current" : "bg-current/40",
        index === active && "bg-brand ring-2 ring-brand/15",
      )}
      style={{
        height:
          scale === "large"
            ? index === stressIndex
              ? 80
              : 48
            : index === stressIndex
              ? 32
              : 20,
      }}
    />
  );

  if (!syllableLabels) {
    return (
      <div className={cn("flex items-end justify-center gap-1", className)}>
        {bars.map((_, index) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: static pattern
            key={index}
          >
            {renderBar(index)}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn("grid w-full", className)}
      style={{
        gridTemplateColumns: `repeat(${syllableCount}, minmax(0, 1fr))`,
      }}
    >
      {bars.map((_, index) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: static pattern
          key={index}
          className="flex min-w-0 flex-col items-center"
        >
          <div className="flex h-8 items-end justify-center">
            {renderBar(index)}
          </div>
          <span
            className={cn(
              "mt-1 flex h-5 max-w-max items-center justify-center rounded-full bg-brand px-1 font-semibold text-[10px] text-primary-foreground leading-none transition-opacity duration-100 motion-reduce:transition-none",
              index === active ? "opacity-100" : "opacity-0",
            )}
            aria-hidden="true"
          >
            {syllableLabels[index]}
          </span>
        </div>
      ))}
    </div>
  );
};
